import { lazy, lazyAppContext } from '@1inch-community/core/lazy';
import { ChainId, } from '@1inch-community/models';
import { asapScheduler, BehaviorSubject, combineLatest, defaultIfEmpty, defer, distinctUntilChanged, filter, firstValueFrom, map, of, shareReplay, startWith, subscribeOn, switchMap, takeUntil, tap, timer, } from 'rxjs';
import { isAddressEqual } from 'viem';
import { adapterId } from './adapter-id';
import { getChainIdsFromStorage, setActiveAddress } from './storage';
export class GlobalDataAdapter {
    constructor(dataProvider) {
        this.dataProvider = dataProvider;
        this.context = lazyAppContext('GlobalDataAdapter');
        this.chainIdInner$ = lazy(() => new BehaviorSubject(getChainIdsFromStorage(this.context.value.storage) ?? [ChainId.eth]));
        this.currentActiveAdapter$ = defer(() => this.dataProvider.update$).pipe(startWith(null), map(() => this.dataProvider.currentActiveAdapter), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));
        this.currentActiveAdapterData$ = this.currentActiveAdapter$.pipe(map((adapter) => adapter?.data ?? null), shareReplay({ bufferSize: 1, refCount: true }));
        this.info$ = this.currentActiveAdapterData$.pipe(filter(Boolean), switchMap((data) => data.info$));
        this.addresses$ = this.currentActiveAdapterData$.pipe(switchMap((data) => data?.addresses$ ?? of([])), shareReplay({ bufferSize: 1, refCount: true }));
        this.activeAddress$ = this.currentActiveAdapterData$.pipe(switchMap((data) => data?.activeAddress$ ?? of(null)), tap((address) => address && setActiveAddress(this.context.value.storage, address)), shareReplay({ bufferSize: 1, refCount: true }));
        // readonly chainId$: Observable<ChainId | null> = combineLatest([
        //   this.currentActiveAdapterData$,
        //   this.chainIdInner$
        // ]).pipe(
        //   switchMap(([data, chainId]) => data?.chainId$ ?? of(chainId)),
        //   tap(chainId => chainId && setChainIdInStorage(this.context.value.storage, chainId)),
        //   shareReplay({ bufferSize: 1, refCount: true })
        // );
        // todo: stub
        this.chainId$ = of(ChainId.eth);
        this.disconnect$ = this.currentActiveAdapterData$.pipe(switchMap((data) => data?.disconnect$ ?? of()), shareReplay({ bufferSize: 1, refCount: true }));
        this.isConnected$ = this.currentActiveAdapterData$.pipe(switchMap((data) => data?.isConnected$ ?? of(false)), shareReplay({ bufferSize: 1, refCount: true }));
        combineLatest([this.activeAddress$, this.chainId$]).pipe(subscribeOn(asapScheduler)).subscribe();
    }
    async init(context) {
        this.context.set(context);
    }
    getInfo() {
        if (!this.dataProvider.currentActiveAdapter)
            throw new Error('');
        return this.dataProvider.currentActiveAdapter.data.getInfo();
    }
    async getAddresses() {
        const data = await this.getProviderDataAdapter();
        if (!data)
            return [];
        return await data.getAddresses();
    }
    async getActiveAddress() {
        if (!this.dataProvider.currentActiveAdapter) {
            return null;
        }
        const data = await this.getProviderDataAdapter();
        if (!data)
            return null;
        return await data.getActiveAddress();
    }
    async getChainId() {
        if (!this.dataProvider.currentActiveAdapter) {
            return this.chainIdInner$.value.value[0];
        }
        const data = await this.getProviderDataAdapter();
        if (!data)
            return this.chainIdInner$.value.value[0];
        const chainId = await data.getChainId();
        if (!chainId)
            return this.chainIdInner$.value.value[0];
        return chainId;
    }
    async isConnected() {
        if (!this.dataProvider.currentActiveAdapter) {
            return false;
        }
        const data = await this.getProviderDataAdapter();
        if (!data)
            return false;
        return await data.isConnected();
    }
    async isActiveAddress(info, address) {
        const id = adapterId(info);
        const activeAddress = (await this.dataProvider.currentActiveAdapter?.data.getActiveAddress()) ?? null;
        const activeAdapterInfo = this.dataProvider.currentActiveAdapter?.data.getInfo() ?? null;
        const activeAdapterId = activeAdapterInfo ? adapterId(activeAdapterInfo) : null;
        return (!!activeAddress &&
            !!activeAdapterId &&
            id === activeAdapterId &&
            isAddressEqual(activeAddress, address));
    }
    isActiveWallet(info) {
        const id = adapterId(info);
        const activeAdapterInfo = this.dataProvider.currentActiveAdapter?.data.getInfo() ?? null;
        const activeAdapterId = activeAdapterInfo ? adapterId(activeAdapterInfo) : null;
        return !!activeAdapterId && activeAdapterId === id;
    }
    isActiveAddress$(info, address) {
        const id = adapterId(info);
        return combineLatest([this.currentActiveAdapter$, this.activeAddress$]).pipe(map(([adapter, activeAddress]) => {
            const activeAdapterId = adapter ? adapterId(adapter.data.getInfo()) : null;
            return (!!activeAddress &&
                !!activeAdapterId &&
                id === activeAdapterId &&
                isAddressEqual(activeAddress, address));
        }));
    }
    isActiveWallet$(info) {
        const id = adapterId(info);
        return this.currentActiveAdapter$.pipe(map((adapter) => {
            const activeAdapterId = adapter ? adapterId(adapter.data.getInfo()) : null;
            return !!activeAdapterId && activeAdapterId === id;
        }));
    }
    setChainIds(chainId) {
        this.chainIdInner$.value.next(chainId);
    }
    getProviderDataAdapter() {
        return firstValueFrom(this.currentActiveAdapterData$.pipe(takeUntil(timer(0)), defaultIfEmpty(null)));
    }
}
//# sourceMappingURL=global-data-adapter.js.map